import { SignUp } from '@clerk/clerk-react'
import { safeInternalPath } from '@/utils/safeInternalPath'
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import OnboardingAuthShell from '@/components/onboarding/OnboardingAuthShell'
import { useSignupFirst } from '@/hooks/useSignupFirst'
import {
  buildClaimRedirect,
  getClaimParams,
  getMarketingUrlParam,
} from '@/utils/onboardingScan'
import {
  buildSubscriptionCheckoutPath,
  buildSubscriptionSignInPath,
  getSubscriptionCheckoutIntent,
} from '@/utils/subscriptionCheckoutIntent'

export default function SignUpPage() {
  const [searchParams] = useSearchParams()
  const { domain } = getClaimParams(searchParams)
  const prefillUrl = getMarketingUrlParam(searchParams)
  const signupFirst = useSignupFirst()

  const redirectUrl = useMemo(() => {
    const claimRedirect = buildClaimRedirect(searchParams, '')
    const checkoutIntent = getSubscriptionCheckoutIntent(searchParams)
    if (checkoutIntent) return buildSubscriptionCheckoutPath(checkoutIntent)
    /*
      Same contract as sign-in: `returnTo` is where the person was going. The
      HubSpot marketplace install sends someone here mid-install, and landing
      them anywhere else would leave HubSpot waiting forever.

      With nothing else claiming the new account it goes to the start page,
      which asks for its website. The claim branch stays for anonymous scans
      still in flight when the switch went on. With the switch off the
      default is the dashboard, as it was.
    */
    const returnTo = safeInternalPath(searchParams.get('returnTo'))
    return claimRedirect || returnTo || (signupFirst ? '/welcome/start' : '/dashboard')
  }, [searchParams, signupFirst])

  const signInUrl = useMemo(() => {
    const claimRedirect = buildClaimRedirect(searchParams, '')
    const checkoutIntent = getSubscriptionCheckoutIntent(searchParams)
    if (checkoutIntent) return buildSubscriptionSignInPath(checkoutIntent)
    if (claimRedirect || prefillUrl || searchParams.get('returnTo')) return `/sign-in?${searchParams.toString()}`
    return '/sign-in'
  }, [prefillUrl, searchParams])

  useEffect(() => {
    document.title = domain
      ? `Unlock ${domain} Site Scan Report | SuperSchema`
      : 'Sign Up | SuperSchema'
  }, [domain])

  /*
    The free sign-up's words ("Create your free account", "Next we ask for
    your website") are true of one destination only: the start page. A plan
    button leads to checkout; the HubSpot install leads back to HubSpot; a
    claim leads to its report. Those keep the plain words.
  */
  const deal = signupFirst && redirectUrl === '/welcome/start'

  return (
    <OnboardingAuthShell mode="sign-up" deal={deal} domain={domain} prefillUrl={prefillUrl} switchHref={signInUrl}>
      <SignUp
        appearance={{
          elements: {
            /*
              Structure only. Everything about how this looks now lives in
              `clerk.css`, in tokens, so both themes come out of the same
              rules instead of a wall of light-theme literals here and sixty
              lines correcting them for dark in index.css.

              These four are layout plumbing that neutralises Clerk's own
              card so it can sit inside ours, plus the offset that centres a
              provider icon beside its label.
            */
            rootBox: 'w-full max-w-[520px] mx-auto flex justify-center overflow-visible',
            cardBox: 'w-full max-w-[520px] mx-auto !shadow-none shadow-none !border-0 border-0 ring-0 bg-transparent overflow-visible',
            card: 'w-full max-w-[520px] mx-auto !shadow-none shadow-none !border-0 border-0 ring-0 p-0 bg-transparent overflow-visible',
            headerTitle: 'hidden',
            headerSubtitle: 'hidden',
            socialButtonsBlockButton: 'relative h-12 w-full justify-center overflow-visible px-6',
            socialButtonsBlockButtonArrow: 'hidden',
            socialButtonsProviderIcon: 'absolute left-1/2 -translate-x-[5.8rem]',
          }
        }}
        forceRedirectUrl={redirectUrl}
        redirectUrl={redirectUrl}
        signInUrl={signInUrl}
        unsafeMetadata={prefillUrl ? { marketingUrl: prefillUrl } : undefined}
      />
    </OnboardingAuthShell>
  )
}
